import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FortisPayment, type FortisPaymentHandle } from '../components/payments/FortisPayment'
import * as paymentsApi from '../api/payments'
import { PaymentPending } from '../components/payments/PaymentPending'
import { PAYMENT_PENDING_TEXT, isDefiniteRefusal, pendingRefusal, serverError } from '../api/paymentOutcome'
import type { MemberDuesInfo, CostItem, FortisTransactionData, BillingDetails } from '../types'

type Step = 'loading' | 'summary' | 'checkout' | 'processing' | 'pending' | 'success' | 'error'

export function Dues() {
  const { user } = useAuth()
  const fortisRef = useRef<FortisPaymentHandle>(null)
  const completedRef = useRef(false)

  const [step, setStep] = useState<Step>('loading')
  const [duesInfo, setDuesInfo] = useState<MemberDuesInfo | null>(null)
  const [error, setError] = useState('')
  const [pendingMessage, setPendingMessage] = useState('')

  // Payment session state
  const [clientToken, setClientToken] = useState('')
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('production')
  const [transactionId, setTransactionId] = useState('')
  const [costItems, setCostItems] = useState<CostItem[]>([])
  const [total, setTotal] = useState(0)

  // Billing form state (pre-filled from user profile)
  const [billing, setBilling] = useState<BillingDetails>({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: user?.phone ?? '',
  })

  // Fetch dues status on mount
  useEffect(() => {
    paymentsApi.getDuesStatus()
      .then((info) => {
        setDuesInfo(info)
        if (info.pendingCapture) {
          setPendingMessage(info.pendingMessage || PAYMENT_PENDING_TEXT)
          setStep('pending')
          return
        }
        setStep('summary')
      })
      .catch(() => {
        setError('Unable to load dues information. Please try again.')
        setStep('error')
      })
  }, [])

  async function handlePayDues() {
    setError('')
    setStep('loading')
    try {
      const result = await paymentsApi.startDuesPayment()
      setClientToken(result.clientToken)
      setEnvironment(result.env)
      setTransactionId(result.transactionId)
      setCostItems(result.costData.items)
      setTotal(result.costData.total)
      setStep('checkout')
    } catch (err) {
      const held = pendingRefusal(err)
      if (held) {
        setPendingMessage(held)
        setStep('pending')
        return
      }
      setError('Unable to start the payment process. Please try again.')
      setStep('summary')
    }
  }

  function validateBilling(): string[] {
    const missing: string[] = []
    if (!billing.firstName.trim()) missing.push('First Name')
    if (!billing.lastName.trim()) missing.push('Last Name')
    if (!billing.address.trim()) missing.push('Address')
    if (!billing.city.trim()) missing.push('City')
    if (!billing.state.trim()) missing.push('State')
    if (!billing.zip.trim()) missing.push('ZIP Code')
    if (!billing.phone.trim()) missing.push('Phone')
    return missing
  }

  function handleSubmitPayment() {
    const missing = validateBilling()
    if (missing.length > 0) {
      setError('Please complete: ' + missing.join(', '))
      return
    }
    setError('')
    setStep('processing')
    fortisRef.current?.submit()
  }

  async function handleFortisSuccess(fortisData: FortisTransactionData) {
    // Complete each payment once from this page. A second 'done' while the first
    // completion is still capturing gets roster's lock-busy answer, and treating
    // that as a refusal would offer Pay Now again mid-capture.
    if (completedRef.current) return
    completedRef.current = true
    try {
      const result = await paymentsApi.completeDuesPayment({
        transactionId,
        fortisTransaction: fortisData,
        billingDetails: billing,
        shippingDetails: billing,
      })
      if (result?.pending) {
        // The payment form is gone and stays gone: the member must not pay again.
        setPendingMessage(result.message || PAYMENT_PENDING_TEXT)
        setStep('pending')
        return
      }
      setStep('success')
    } catch (err) {
      completedRef.current = false
      if (isDefiniteRefusal(err)) {
        // The server refused this order for good: Pay again starts a new one.
        setError(serverError(err) ?? 'There was an issue completing your payment. Please contact the AU office.')
        setStep('summary')
        return
      }
      setError('There was an issue completing your payment. Please contact the AU office.')
      setStep('checkout')
    }
  }

  function handleFortisError(msg: string) {
    setError(msg)
    setStep('checkout')
  }

  function updateBilling(field: keyof BillingDetails, value: string) {
    setBilling((prev) => ({ ...prev, [field]: value }))
  }

  const formatMoney = (amount: number) =>
    '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // -- Loading --
  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  // -- Payment not yet confirmed --
  if (step === 'pending') {
    return <PaymentPending message={pendingMessage} />
  }

  // -- Error (failed to load) --
  if (step === 'error' && !duesInfo) {
    return (
      <div className="max-w-lg mx-auto mt-8">
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      </div>
    )
  }

  // -- Success --
  if (step === 'success') {
    return (
      <div className="max-w-lg mx-auto mt-8 text-center">
        <div className="bg-success/10 border border-success/30 rounded-lg p-8">
          <h1 className="text-2xl font-heading font-bold text-success mb-3">Payment Successful</h1>
          <p className="text-text-light mb-2">
            Your {duesInfo?.memberType} dues of <strong>{formatMoney(total)}</strong> have been paid.
          </p>
          <p className="text-text-muted text-sm mb-6">
            A confirmation will be sent to your email on file.
          </p>
          <Link
            to="/dashboard"
            className="inline-block px-6 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  // -- Summary (dues already paid) --
  if (duesInfo?.duesPd) {
    return (
      <div className="max-w-lg mx-auto mt-8">
        <h1 className="text-2xl font-heading font-bold text-text mb-4">Dues</h1>
        <div className="bg-success/10 border border-success/30 rounded-lg p-6">
          <p className="text-success font-medium text-lg mb-1">Your dues are current</p>
          <p className="text-text-muted text-sm">
            Membership type: {duesInfo.memberType}
            {duesInfo.duesPdDate && <> &middot; Paid {duesInfo.duesPdDate}</>}
          </p>
        </div>
        <Link
          to="/dashboard"
          className="inline-block mt-6 text-primary hover:underline text-sm"
        >
          &larr; Back to Dashboard
        </Link>
      </div>
    )
  }

  // -- Summary (dues owed) --
  if (step === 'summary' && duesInfo) {
    return (
      <div className="max-w-lg mx-auto mt-8">
        <h1 className="text-2xl font-heading font-bold text-text mb-4">Pay Dues</h1>

        <div className="bg-surface border border-border rounded-lg p-6 mb-6">
          <p className="text-text-light mb-4">
            Membership: <strong>{duesInfo.memberType}</strong>
          </p>

          {duesInfo.familyMembers.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-text mb-2">Family members included:</p>
              <ul className="text-sm text-text-light space-y-1">
                {duesInfo.familyMembers.map((fm) => (
                  <li key={fm.auid}>
                    {fm.first} {fm.last} ({fm.class})
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-border pt-4">
            <div className="flex justify-between text-lg font-semibold text-text">
              <span>Total Due</span>
              <span>{formatMoney(duesInfo.amount)}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm mb-4">
            {error}
          </div>
        )}

        <button
          onClick={handlePayDues}
          className="w-full py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
        >
          Pay Now
        </button>

        <Link
          to="/dashboard"
          className="inline-block mt-4 text-primary hover:underline text-sm"
        >
          &larr; Back to Dashboard
        </Link>
      </div>
    )
  }

  // -- Checkout (billing form + card) --
  return (
    <div className="max-w-2xl mx-auto mt-8">
      <h1 className="text-2xl font-heading font-bold text-text mb-6">Checkout</h1>

      {/* Order summary */}
      <div className="bg-surface-alt border border-border rounded-lg p-4 mb-6">
        {costItems.map((item, i) => (
          <div key={i} className="flex justify-between text-sm text-text-light py-1">
            <span>{item.label}</span>
            <span>{formatMoney(item.amount)}</span>
          </div>
        ))}
        <div className="flex justify-between font-semibold text-text border-t border-border mt-2 pt-2">
          <span>Total</span>
          <span>{formatMoney(total)}</span>
        </div>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Billing details */}
      <div className="mb-6">
        <h2 className="text-lg font-heading font-semibold text-text mb-3">Billing Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <BillingInput label="First Name" value={billing.firstName} onChange={(v) => updateBilling('firstName', v)} />
          <BillingInput label="Last Name" value={billing.lastName} onChange={(v) => updateBilling('lastName', v)} />
          <BillingInput label="Address" value={billing.address} onChange={(v) => updateBilling('address', v)} className="sm:col-span-2" />
          <BillingInput label="City" value={billing.city} onChange={(v) => updateBilling('city', v)} />
          <div className="grid grid-cols-2 gap-3">
            <BillingInput label="State" value={billing.state} onChange={(v) => updateBilling('state', v)} maxLength={2} />
            <BillingInput label="ZIP" value={billing.zip} onChange={(v) => updateBilling('zip', v)} />
          </div>
          <BillingInput label="Phone" value={billing.phone} onChange={(v) => updateBilling('phone', v)} type="tel" className="sm:col-span-2" />
        </div>
      </div>

      {/* Fortis payment fields */}
      <div className="mb-6">
        <h2 className="text-lg font-heading font-semibold text-text mb-3">Payment</h2>
        <FortisPayment
          ref={fortisRef}
          clientToken={clientToken}
          environment={environment}
          onSuccess={handleFortisSuccess}
          onError={handleFortisError}
        />
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmitPayment}
        disabled={step === 'processing'}
        className="w-full py-3 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {step === 'processing' ? 'Processing...' : `Pay ${formatMoney(total)}`}
      </button>

      <button
        onClick={() => setStep('summary')}
        disabled={step === 'processing'}
        className="w-full mt-3 py-2 text-text-muted hover:text-text text-sm transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
    </div>
  )
}

function BillingInput({ label, value, onChange, type = 'text', maxLength, className = '' }: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  maxLength?: number
  className?: string
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-text mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
      />
    </div>
  )
}
